import classnames from "classnames";
import { isLeft } from "fp-ts/lib/Either";
import { inject } from "mobx-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { CalendarPayloadFields, CalendarPayloadResolver } from "@ihr-radioedit/inferno-core";
import * as Webapi from "@ihr-radioedit/inferno-webapi";
import { getEventVenueAddress, getEventVenueName } from "../../../../core/lib/calendar";
import { getPaths } from "@ihr-radioedit/inferno-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { reverseRoute } from "@inferno/renderer-shared-core";
import { Heading, MagicLink, DisplayTime, Venue } from "../../../../core/ui";
import styles from "./CoastCalendarFrontMatter.module.scss";
import type { Store } from "@inferno/renderer-shared-core";

const log = ILog.logger("Calendar FrontMatter");

interface FrontMatterProps {
  content: Webapi.PublishRecordFragment;
  block: Webapi.Block_SitesItemBlock_Fragment;
  eyebrow?: Webapi.Eyebrow;
  store?: Store;
}

const EventDates = ({
  event_date,
  hide_event_start_time,
  hide_event_date,
  store,
}: CalendarPayloadFields & { store: Store }) => {
  const { t } = useTranslation();
  const [clicked, storeClicked] = useState(false);

  const toggleClicked = useCallback(() => {
    storeClicked(!clicked);
  }, [clicked, storeClicked]);

  if (!!event_date?.value.length) {
    const startDateValue = event_date.value[0];
    const startDate = startDateValue?.begin ? (
      <DisplayTime
        store={store}
        startDate={startDateValue.begin}
        hideEventStartTime={!!hide_event_start_time?.value}
        hideEventDate={!!hide_event_date?.value}
      />
    ) : null;

    const additionalDatesValue = event_date.value.slice(1, event_date.value.length);

    return (
      <>
        <div>
          {startDate}
          {additionalDatesValue?.length ? (
            <span className="additional-dates-text">
              (
              <span onClick={toggleClicked}>
                +{additionalDatesValue.length} {t("calendar_occurrences")}
              </span>
              )
            </span>
          ) : null}
        </div>
        {clicked ? (
          <div className="additional-dates">
            {additionalDatesValue.map((date, i) => {
              return (
                date?.begin && (
                  <DisplayTime
                    key={i}
                    store={store}
                    startDate={date.begin}
                    hideEventStartTime={!!hide_event_start_time?.value}
                    hideEventDate={!!hide_event_date?.value}
                  />
                )
              );
            })}
          </div>
        ) : null}
      </>
    );
  }
  return null;
};

export const CoastCalendarFrontMatter = inject("store")(({ content, block, store }: FrontMatterProps) => {
  if (!store || !content || !block) {
    return null;
  }

  const { site } = store;

  const resolvedPayload = CalendarPayloadResolver.decode(content.payload);
  if (isLeft(resolvedPayload)) {
    const errorMessage = `Payload did not validate! ${getPaths(resolvedPayload)}`;
    log.error(errorMessage);
    return null;
  }
  const payload = resolvedPayload.right;
  const { fields } = payload;

  if (!fields) {
    return null;
  }

  const headerCss = classnames(styles.contentHeader);

  const eyebrowUrl = useMemo(() => reverseRoute(site, "calendar_content_all"), [site]) || "/calendar/";

  return (
    <header className={headerCss}>
      <nav className="component-eyebrow">
        <MagicLink to={eyebrowUrl}>Calendar</MagicLink>
      </nav>
      <Heading level={1}>{content.summary.title}</Heading>
      <hr />
      <Venue venueName={getEventVenueName(fields)} venueAddress={getEventVenueAddress(fields)} />
      <EventDates store={store} {...fields} />
    </header>
  );
});

export default CoastCalendarFrontMatter;
